import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getSupabase } from "@/lib/supabase";

type Status = "idle" | "loading" | "success" | "error";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      const supabase = getSupabase();
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        setHasSession(true);
      } else {
        setHasSession(false);
        setStatus("error");
        setMessage("Invalid or expired reset link. Please request a new one.");
      }
    };
    checkSession();
  }, [location]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      setMessage("Password has been reset successfully!");
      setTimeout(() => navigate("/"), 2000);
      return;
    }

    const password = (formData.get("password") as string) || "";
    const confirmPassword = (formData.get("confirm_password") as string) || "";

    if (password !== confirmPassword) {
      setStatus("error");
      setMessage("Passwords do not match.");
      return;
    }

    if (password.length < 6) {
      setStatus("error");
      setMessage("Password must be at least 6 characters.");
      return;
    }

    setStatus("loading");
    setMessage("");

    const supabase = getSupabase();
    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      setStatus("error");
      setMessage(error.message);
    } else {
      setStatus("success");
      setMessage("Password has been reset successfully!");
      if (formRef.current) formRef.current.reset();
      setTimeout(() => navigate("/"), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#161b22] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-[#0d1117] rounded-lg shadow-sm border border-gray-700 overflow-hidden">
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-center mb-3">
              <div className="w-12 h-12 bg-[#5cb85c] rounded-full flex items-center justify-center">
                <i className="fas fa-lock text-white text-lg" />
              </div>
            </div>
            <h1 className="text-lg font-bold text-gray-200 text-center">Reset Your Password</h1>
            <p className="text-sm text-gray-500 text-center mt-1">
              Enter a new password for your account.
            </p>
          </div>

          <div className="px-6 pb-6">
            {hasSession === null && (
              <div className="flex items-center justify-center py-8">
                <i className="fas fa-spinner fa-spin text-[#5cb85c] text-xl" />
              </div>
            )}

            {hasSession === true && (
              <form ref={formRef} onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 pointer-events-none"
                />

                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">New Password</label>
                  <input
                    type="password"
                    name="password"
                    required
                    minLength={6}
                    placeholder="At least 6 characters"
                    className="w-full px-3 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">Confirm New Password</label>
                  <input
                    type="password"
                    name="confirm_password"
                    required
                    minLength={6}
                    placeholder="Re-enter your password"
                    className="w-full px-3 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                  />
                </div>

                {status === "error" && message && (
                  <div className="mb-4 p-3 bg-red-900/20 border border-red-700/30 rounded text-sm text-red-400">
                    <i className="fas fa-exclamation-circle mr-2" />{message}
                  </div>
                )}

                {status === "success" && message && (
                  <div className="mb-4 p-3 bg-green-900/20 border border-green-700/30 rounded text-sm text-green-400">
                    <i className="fas fa-check-circle mr-2" />{message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded transition-all whitespace-nowrap"
                >
                  {status === "loading" ? (
                    <span><i className="fas fa-spinner fa-spin mr-2" />Resetting...</span>
                  ) : (
                    "RESET PASSWORD"
                  )}
                </button>
              </form>
            )}

            {hasSession === false && status === "error" && (
              <>
                <div className="mb-4 p-3 bg-red-900/20 border border-red-700/30 rounded text-sm text-red-400">
                  <i className="fas fa-exclamation-circle mr-2" />{message}
                </div>
                <button
                  onClick={() => navigate("/")}
                  className="w-full bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold py-2.5 rounded transition-all whitespace-nowrap"
                >
                  BACK TO HOME
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}